import { useEffect, useRef, useState } from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import useModals from 'src/Hooks/useModals'
import usePortalQuery from 'src/Libs/api/hooks/use-portal-query'
import { useTypedSelector } from 'src/State/RootReducer'
import { fetchShopPopupMessages } from '../api/shop-popup-message.api'
import { ShopPopupMessageLocation } from '../models/shop-popup-message.model'
import { isShopPopupMessageRead } from '../utils/shop-popup-message-session'
import { useShopFetchWithResponse } from './use-shop-fetch'
import useEffectShopAccess from './use-shop-access-use-effect'

/**
 * Displays popup messages configured for a shop page location.
 */
export const useShopPopupMessages = (location: ShopPopupMessageLocation): void => {
  const shopFetch = useShopFetchWithResponse()
  const { openShopPopupMessagesModal } = useModals()
  const { selectedAppLanguage } = useCustomTranslation()
  const openedMessageHashes = useRef(new Set<string>())
  const shopContextToken = useTypedSelector(state => state.shop.selectedUserData?.contextToken ?? state.shop.contextToken)
  const [isShopReady, setIsShopReady] = useState(false)

  useEffectShopAccess(() => setIsShopReady(true), [shopContextToken])

  const { data } = usePortalQuery({
    queryKey: ['shop-popup-messages', shopContextToken, selectedAppLanguage],
    queryFn: () => fetchShopPopupMessages(shopFetch),
    enabled: isShopReady,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  })

  useEffect(() => {
    const messagesToShow = (data ?? []).filter(message => {
      return (
        message.locations.includes(location) && !isShopPopupMessageRead(message.contentHash) && !openedMessageHashes.current.has(message.contentHash)
      )
    })

    if (messagesToShow.length === 0) return

    for (const message of messagesToShow) {
      openedMessageHashes.current.add(message.contentHash)
    }
    openShopPopupMessagesModal({ messages: messagesToShow })
  }, [data, location, openShopPopupMessagesModal])
}
